import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { convertImageUrlToBase64 } from "../components/ContractorBadge/utils/convertImageUrlToBase64";

export const getVerificationSelfieResponseSchema = z.object({
  data: z
    .object({
      id: z.string(),
      type: z.string(),
      attributes: z.object({
        expireableFileUrl: z.string(),
      }),
    })
    .nullable(),
});

export const getVerificationSelfieRequestSchema = z.object({
  filter: z.object({
    workerId: z.string(),
  }),
});

type GetVerificationSelfieRequest = z.infer<typeof getVerificationSelfieRequestSchema>;

export type GetVerificationSelfieResponse = z.infer<typeof getVerificationSelfieResponseSchema>;

const url = `${environmentConfig.REACT_APP_IDENTITY_DOC_AUTOVERIFICATION_SERVICE_API_URL}/verification-session-selfies`;

const baseQueryKey = "verification-selfie-image";
export function resetGetVerificationSelfieQuery(queryClient: QueryClient) {
  void queryClient.resetQueries({ queryKey: [baseQueryKey] });
}

// Marking the data stale after 15 minutes to avoid making too many requests to the server.
// For a worker it is very unlikely that the selfie will change in 15 minutes.
const TIME_UNTIL_STALE_OR_GARBAGE_COLLECTED = 1000 * 60 * 15;

export function useGetVerificationSelfie(
  props: GetVerificationSelfieRequest,
  options: {
    refetchOnWindowFocus?: boolean;
  }
) {
  return useQuery({
    queryKey: [baseQueryKey, props.filter.workerId],
    queryFn: async () => {
      const { data } = await get({
        url,
        queryParams: {
          filter: {
            workerId: props.filter.workerId,
          },
        },
        responseSchema: getVerificationSelfieResponseSchema,
        requestSchema: getVerificationSelfieRequestSchema,
      });
      const responseData = data?.data;
      if (!responseData?.attributes.expireableFileUrl) {
        return "";
      }

      return await convertImageUrlToBase64(responseData.attributes.expireableFileUrl);
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_VERIFICATION_SELFIE_FAILURE,
    },

    staleTime: TIME_UNTIL_STALE_OR_GARBAGE_COLLECTED,
    cacheTime: TIME_UNTIL_STALE_OR_GARBAGE_COLLECTED,
    ...options,
  });
}
